import { Inject, Injectable, PLATFORM_ID, WritableSignal, inject, signal } from "@angular/core";
import { ApiService } from "../../../shared/api.service";
import { BehaviorSubject, filter, finalize, map, Observable, tap } from "rxjs";
import { RequestHandlerParams } from "../../../shared/types/api-service.types";
import { ApiResponse } from "../../../shared/common/types";
import { isPlatformBrowser } from "@angular/common";
import { CollectJSValidationResult, initialCollectJSValidationResult } from "./payment-method.types";
import { NotificationService } from "../../../shared/notification/notification.service";

// This var is for CollectJS for NMI configuration:
declare var CollectJS: any;

@Injectable({
    providedIn: 'root'
})
export class PaymentMethodService {

    private apiService = inject(ApiService);
    private notificationService = inject(NotificationService);

    paymentMethod: WritableSignal<any> = signal(null);
    isLoadingContent = signal(true);

    #collectJSValidation: BehaviorSubject<CollectJSValidationResult> = new BehaviorSubject(initialCollectJSValidationResult);
    collectJSValidation$: Observable<CollectJSValidationResult> = this.#collectJSValidation.asObservable();

    #collectJSResponse: BehaviorSubject<any> = new BehaviorSubject(null);
    collectJSResponse$: Observable<any> = this.#collectJSResponse.asObservable();

    #collectJSTimeout: BehaviorSubject<boolean> = new BehaviorSubject(false);
    collectJSTimeout$: Observable<boolean> = this.#collectJSTimeout.asObservable();

    private endpoints = {
        paymentMethod: '/account/payment-method',
        states: '/states'
    }

    constructor(@Inject(PLATFORM_ID) private platformId: any) { }

    getPaymenthMethodInfo() {

        this.isLoadingContent.set(true);

        const params: RequestHandlerParams = {
            method: 'GET',
            endpoint: this.endpoints.paymentMethod,
            apiV3: true
        };

        this.apiService.handleRequest(params).pipe(
            tap((response: any) => this.paymentMethod.set(response?.data)),
            finalize(() => this.isLoadingContent.set(false)),
        ).subscribe();
    }

    getStates() {
        const params: RequestHandlerParams = {
            method: 'GET',
            endpoint: this.endpoints.states,
            apiV3: true,
            showErrorMessage: true
        };

        return this.apiService.handleRequest<ApiResponse<{ code: string, name: string }[]>>(params).pipe(
            filter(res => !!res.data?.length),
            map(res => res.data.map(e => { return { id: e.code, name: e.name } }))
        );
    }

    setUpNMI() {
        if (!isPlatformBrowser(this.platformId) || !CollectJS) return;
        CollectJS.configure({
            'paymentSelector': '#nmiPayButton',
            'variant': 'inline',
            'styleSniffer': true,
            'fields': {
                'ccnumber': {
                    'selector': '#nmi_ccnumber',
                    'title': 'Card Number',
                    'placeholder': '0000 0000 0000 0000',
                },
                'ccexp': {
                    'selector': '#nmi_ccexp',
                    'title': 'Card Expiration',
                    'placeholder': '00 / 00'
                },
                'cvv': {
                    'selector': '#nmi_cvv',
                    'title': 'CVV Code',
                    'placeholder': '***'
                },
            },
            'customCss': {
                'width': '100%',
                'height': '48px',
                'padding': '.375rem .75rem',
                'font-size': '16px',
                'border-radius': '2px',
                'border': '1px solid #E4E4E4',
                'transition': 'border-color .15s ease-in-out, box-shadow .15s ease-in-out',
            },
            'invalidCss': {
                'border-color': '#dc3545'
            },
            'validationCallback': (field: string, valid: any, message: string) => this.validationCallback(field, valid, message),
            "timeoutDuration": 10000,
            "timeoutCallback": () => this.timeoutCallback(),
            "fieldsAvailableCallback": function () {
                console.log("Collect.js loaded the fields onto the form");
            },
            'callback': (response: any) => this.callback(response)
        })
    }

    validationCallback(field: any, valid: boolean, message: string) {
        message = message === 'Field is empty' ? 'This field is required' : message;
        this.#collectJSValidation.next({ type: field, valid, message });
    }

    callback(response: any) {
        if (!response || !response?.card?.number || !response.token) {
            console.log('****************************************');
            console.error('CollectJS response is invalid', response);
            console.log('****************************************');

            this.notificationService.show({ text: 'Payment processing failed. Please try again.', type: 'error' });
            this.#collectJSResponse.next(null);
            return;
        }
        const splittedCardNumber = response.card.number.split('*');
        this.#collectJSResponse.next({ card: splittedCardNumber[splittedCardNumber.length - 1], token: response.token, exp: response.card.exp });
    }

    timeoutCallback() {
        this.#collectJSTimeout.next(true);
    }

    clearCollectJSResponse() {
        this.#collectJSResponse.next(null);
    }
}